import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const ExamTakingModal = ({ exam, open, onClose, onSubmit }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(exam?.time_limit_minutes * 60 || 0);
  const [startTime] = useState(Date.now());
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    if (!open || !exam) return;
    
    setTimeRemaining(exam.time_limit_minutes * 60);
    setCurrentQuestionIndex(0);
    setAnswers({});
  }, [open, exam]);

  useEffect(() => {
    if (!open || timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [open, timeRemaining]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswerChange = (questionId, answer) => {
    setAnswers({
      ...answers,
      [questionId]: answer
    });
  };

  const handleSubmit = () => {
    const timeTaken = Math.floor((Date.now() - startTime) / 1000);
    const answersArray = exam.questions.map(q => ({
      question_id: q.id,
      answer: answers[q.id] || ''
    }));

    onSubmit({
      exam_id: exam.id,
      answers: answersArray,
      time_taken_seconds: timeTaken
    });
    
    onClose();
  };

  const confirmSubmit = () => {
    const unanswered = exam.questions.filter(q => !answers[q.id]).length;
    if (unanswered > 0) {
      setShowConfirmDialog(true);
    } else {
      handleSubmit();
    }
  };

  if (!exam) return null;

  const currentQuestion = exam.questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / exam.questions.length) * 100;
  const answeredCount = Object.keys(answers).filter(k => answers[k]).length;

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{exam.title}</span>
              <div className="flex items-center gap-4">
                <Badge variant={timeRemaining < 300 ? 'destructive' : 'default'} className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {formatTime(timeRemaining)}
                </Badge>
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Progress Bar */}
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span>Question {currentQuestionIndex + 1} of {exam.questions.length}</span>
                <span>{answeredCount} answered</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>

            {/* Current Question */}
            <Card className="p-6">
              <div className="mb-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-semibold">Question {currentQuestionIndex + 1}</h3>
                  <Badge>{currentQuestion.points} {currentQuestion.points === 1 ? 'point' : 'points'}</Badge>
                </div>
                <p className="text-gray-700">{currentQuestion.question_text}</p>
              </div>

              {/* Answer Options */}
              {currentQuestion.question_type === 'mcq' && (
                <RadioGroup
                  value={answers[currentQuestion.id] || ''}
                  onValueChange={(value) => handleAnswerChange(currentQuestion.id, value)}
                >
                  <div className="space-y-3">
                    {currentQuestion.options.map((option, idx) => (
                      <div key={idx} className="flex items-center space-x-2 p-3 border rounded hover:bg-gray-50">
                        <RadioGroupItem value={String.fromCharCode(65 + idx)} id={`option-${idx}`} />
                        <Label htmlFor={`option-${idx}`} className="flex-1 cursor-pointer">
                          {String.fromCharCode(65 + idx)}. {option}
                        </Label>
                      </div>
                    ))}
                  </div>
                </RadioGroup>
              )}

              {currentQuestion.question_type === 'true_false' && (
                <RadioGroup
                  value={answers[currentQuestion.id] || ''}
                  onValueChange={(value) => handleAnswerChange(currentQuestion.id, value)}
                >
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2 p-3 border rounded hover:bg-gray-50">
                      <RadioGroupItem value="True" id="true-option" />
                      <Label htmlFor="true-option" className="flex-1 cursor-pointer">True</Label>
                    </div>
                    <div className="flex items-center space-x-2 p-3 border rounded hover:bg-gray-50">
                      <RadioGroupItem value="False" id="false-option" />
                      <Label htmlFor="false-option" className="flex-1 cursor-pointer">False</Label>
                    </div>
                  </div>
                </RadioGroup>
              )}

              {currentQuestion.question_type === 'short_answer' && (
                <Textarea
                  value={answers[currentQuestion.id] || ''}
                  onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                  placeholder="Type your answer here..."
                  rows={4}
                  className="w-full"
                />
              )}
            </Card>

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <Button
                variant="outline"
                onClick={() => setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1))}
                disabled={currentQuestionIndex === 0}
              >
                Previous
              </Button>

              <div className="flex gap-2">
                {exam.questions.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentQuestionIndex(idx)}
                    className={`w-8 h-8 rounded ${
                      idx === currentQuestionIndex
                        ? 'bg-blue-600 text-white'
                        : answers[exam.questions[idx].id]
                        ? 'bg-green-200 text-green-800'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {idx + 1}
                  </button>
                ))}
              </div>

              {currentQuestionIndex < exam.questions.length - 1 ? (
                <Button
                  onClick={() => setCurrentQuestionIndex(currentQuestionIndex + 1)}
                >
                  Next
                </Button>
              ) : (
                <Button
                  onClick={confirmSubmit}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Submit Exam
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-600" />
              Confirm Submission
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="mb-2">You have unanswered questions:</p>
            <p className="font-semibold">
              Answered: {answeredCount} / {exam.questions.length}
            </p>
            <p className="text-sm text-gray-600 mt-2">
              Are you sure you want to submit? Unanswered questions will be marked as incorrect.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Continue Exam
            </Button>
            <Button onClick={handleSubmit} className="bg-green-600 hover:bg-green-700">
              Submit Anyway
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ExamTakingModal;
